<template>
	<v-card>
		<v-card-text>
			<div class="d-flex flex-row align-center my-4 w-100">
				<!-- Start Date Time-->
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> Start Date Time</label>
					<div class="d-flex">
						<v-menu v-model="datePickerOpened.StartDateTime" :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedStartDateTime }}
								</span>
								</v-btn>
							</template>
							<v-date-picker
								v-model="formData.StartDateTime"
								color="secondary"
								hide-header
								show-adjacent-months
								:disabled="!editStationStartDateTimeFeatureEnabled"
								@update:model-value="datePickerOpened.StartDateTime = false">
							</v-date-picker>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							@click.stop="formData.StartDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>

				<!-- End Date Time-->
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> End Date Time</label>
					<div class="d-flex">
						<v-menu v-model="datePickerOpened.EndDateTime" :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedEndDateTime }}
								</span>
								</v-btn>
							</template>
							<v-date-picker
								v-model="formData.EndDateTime"
								color="secondary"
								hide-header
								show-adjacent-months
								@update:model-value="datePickerOpened.EndDateTime = false">
							</v-date-picker>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							@click.stop="formData.EndDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>

				<!-- Modified Date Time-->
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> Modified Date Time</label>
					<div class="d-flex">
						<v-menu v-model="datePickerOpened.ModifiedDateTime" :close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" class="flex-grow-1" variant="outlined">
									<v-img width="20" height="20" src="@/assets/images/Calendar.svg"/>
									<span class="ml-3 text-secondary">
									{{ formattedModifiedDateTime }}
								</span>
								</v-btn>
							</template>
							<v-date-picker
								v-model="formData.ModifiedDateTime"
								color="secondary"
								hide-header
								show-adjacent-months
								@update:model-value="datePickerOpened.ModifiedDateTime = false">
							</v-date-picker>
						</v-menu>
						<v-btn icon class="ml-2"
							density="comfortable" rounded="lg" variant="outlined"
							@click.stop="formData.ModifiedDateTime = null"
						>
							<v-icon icon="mdi-close" size="24"/>
						</v-btn>
					</div>
				</div>
			</div>
			<div class="d-flex flex-row align-center my-4 w-100">
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold">Display Title</label>
					<v-text-field v-model="formData.DisplayTitle"
						variant="outlined"
						density="compact"
						placeholder="Display Title"
						hide-details
					/>
				</div>
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold">Display Artist</label>
					<v-text-field v-model="formData.DisplayArtist"
						variant="outlined"
						density="compact"
						placeholder="Display Artist"
						hide-details
					/>
				</div>
				<div class="d-flex flex-column ml-4 mr-2 w-100">
					<label class="text-caption font-weight-bold"> Split Behavior </label>
					<v-select v-model="formData.SplitBehavior"
						:items="splitBehaviorItems"
						density="compact"
						variant="outlined"
						hide-details
					/>
				</div>
			</div>

			<v-divider/>

			<v-table>
				<thead>
				<tr>
					<th>24h</th>
					<th v-for="(hour, hIndex) in hours" :key="hour" @click="toggleHour(hIndex)">{{ hour }}</th>
				</tr>
				</thead>
				<tbody>
				<tr v-for="(day, dIndex) in getEnumDays()" :key="day">
					<td @click="toggleDay(dIndex)">{{ day }}</td>
					<td v-for="(hour, hIndex) in hours" :key="hIndex">
						<v-icon
							:icon="schedule[dIndex][hIndex] ? 'mdi-check-circle' :
											'mdi-checkbox-blank-circle-outline'"
							:color="schedule[dIndex][hIndex] ? 'green darken-2' : 'grey'"
							@click="toggleCell(dIndex, hIndex)"
						/>
					</td>
				</tr>
				</tbody>
			</v-table>

			<v-divider/>

			<div class="d-flex my-4">
				<div class="d-flex flex-column w-50">
					<div class="d-inline-flex">
						<v-icon icon="mdi-file"/>
						<span class="d-flex align-center text-caption ml-2"> Station Copy Tool </span>
					</div>
					<v-radio-group v-model="copyOption" hide-details>
						<v-radio label="Station Group" :value="0" :ripple="false" disabled/>
						<v-radio label="Copy above values to Active Stations" :value="1"
							:disabled="!copyToActiveStationsFeatureEnabled"/>
						<v-radio label="Copy above values to All Stations" :value="2"
							:disabled="!copyToAllStationsFeatureEnabled"/>
					</v-radio-group>
					<v-btn color="secondary" max-width="150" prepend-icon="mdi-content-copy" @click="handleCopy">
						Copy
					</v-btn>
				</div>

				<!--todo: enable this feature when we have the station group feature -->
				<!--<div class="d-flex flex-column w-50 h-50">
					<span class="text-caption"> Station Group </span>
					<v-select v-model="gSelectorSelectedCategory"
						:items="gSelectorCategories"
						density="compact"
						variant="outlined"
						hide-details
						:loading="gSelectorLoading"
						:disabled="gSelectorLoading"
					/>
				</div>-->
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
/* Libraries */
import moment from "moment-timezone";
import emitter from "@/emitter";
import _ from "lodash";
import SplitBehaviorTypes from "@/enums/SplitBehaviorTypes";

export default {
	name: "AssetManagementStationDataTab",

	data() {
		return {
			formData: {},
			datePickerOpened: {
				StartDateTime: false,
				EndDateTime: false,
				ModifiedDateTime: false,
			},
			splitBehaviorItems: SplitBehaviorTypes.getArray(),
			daysEnum: {
				"Sunday": 0,
				"Monday": 1,
				"Tuesday": 2,
				"Wednesday": 3,
				"Thursday": 4,
				"Friday": 5,
				"Saturday": 6
			},
			hours: Array.from({length: 24}, (_, i) => i),
			schedule: Array(7).fill().map(() => Array(24).fill(true)), // all selected by default,

			gSelectorSelectedCategory: null,
			gSelectorCategories: [],
			copyOption: 1,
			gSelectorLoading: false
		}
	},

	computed: {
		asset() {
			return this.$store.getters.getAssetManagementAsset();
		},

		selectedStation() {
			return this.$store.getters.getAssetManagementSelectedStation()
		},

		allStationsList() {
			return this.$store.getters.getAssetManagementAllStationsList();
		},

		activeStationsList() {
			return this.$store.getters.getAssetManagementActiveStationsList();
		},

		activeStation() {
			return this.activeStationsList.find(s => s.StationID === this.selectedStation?.StationID);
		},

		formattedStartDateTime() {
			return this.formData.StartDateTime ?
				   moment(this.formData.StartDateTime).format('MMM D, YYYY') :
				   "Start Date Time"
		},

		formattedEndDateTime() {
			return this.formData.EndDateTime ?
				   moment(this.formData.EndDateTime).format('MMM D, YYYY') :
				   "End Date Time"
		},

		formattedModifiedDateTime() {
			return this.formData.ModifiedDateTime ?
				   moment(this.formData.ModifiedDateTime).format('MMM D, YYYY') :
				   "Modified Date Time"
		},

		copyToActiveStationsFeatureEnabled() {
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_copy_station_values_active",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		copyToAllStationsFeatureEnabled() {
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_copy_station_values_all",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		editStationStartDateTimeFeatureEnabled() {
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit_station_startdate",
				asset_type_id: this.asset.AssetTypeID
			});
		}
	},

	methods: {
		initStationData() {
			this.gSelectorLoading = true;
			Object.assign(this.formData, this.activeStation);
			this.formData.ModifiedDateTime = this.formData.ModifiedDateTime ?
											 moment(this.formData.ModifiedDateTime) :
											 null;
			if (_.isNil(this.formData.SplitBehavior)) {
				this.formData.SplitBehavior = 0;
			}

			this.handleHourRestrictions();

			this.$store.dispatch("getGSelectorCategoriesList", {
				StationID: this.selectedStation.StationID,
				AssetTypeID: this.asset.AssetTypeID,
			}).then(res => {
				this.gSelectorCategories = res.data.map(category => {
					return {
						title: category.name,
						value: category.categoryGroup
					}
				})

				this.$store.dispatch("getAssetGSelectorCategory", {
					StationID: this.selectedStation.StationID,
					AssetID: this.asset.AssetID,
				}).then(res => {
					this.gSelectorLoading = false;
					const selectedCategory = res.data
					this.gSelectorSelectedCategory = this.gSelectorCategories.find(sc => sc.value ===
																						 selectedCategory?.categoryGroup);
				});
			})
		},

		toggleCell(dayIndex, hourIndex) {
			this.schedule[dayIndex][hourIndex] = !this.schedule[dayIndex][hourIndex];
		},

		toggleDay(dayIndex) {
			this.schedule[dayIndex].forEach((hourEnabled, hourIndex) => {
				this.schedule[dayIndex][hourIndex] = !hourEnabled;
			})
		},

		toggleHour(hourIndex) {
			this.schedule.forEach((dayEnabled, dayIndex) => {
				this.schedule[dayIndex][hourIndex] = !dayEnabled[hourIndex];
			});
		},

		getEnumDays() {
			return Object.keys(this.daysEnum);
		},

		handleHourRestrictions() {
			if (this.formData.HourRestriction) {
				for (const [day, hours] of Object.entries(this.formData.HourRestriction)) {
					const dayIndex = this.daysEnum[day];
					for (const hour of hours) {
						this.schedule[dayIndex][hour] = false;
					}
				}
			}
		},

		getHourRestrictionFromSchedule() {
			const HourRestriction = {};

			// Reverse the dayMap: index -> dayName
			const indexToDay = Object.entries(this.daysEnum).reduce((acc, [day, index]) => {
				acc[index] = day;
				return acc;
			}, {});

			for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
				const dayName = indexToDay[dayIndex];
				const falseHours = [];

				for (let hour = 0; hour < 24; hour++) {
					if (!this.schedule[dayIndex][hour]) {
						falseHours.push(hour);
					}
				}

				HourRestriction[dayName] = falseHours;
			}

			return HourRestriction;
		},

		updateAsset() {
			this.formData.HourRestriction = this.getHourRestrictionFromSchedule();
			this.$store.dispatch("updateStationAsset", _.omit(this.formData, ["AssetTypeID", "StationName"]))
				.then(() => {
					this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
						message: "Station Data has been updated successfully."
					});
				})
				.finally(() => {
					emitter.emit("stationTab:assetSaved");
				})
		},

		handleCopy() {
			if (this.copyOption === 1) {
				this.copyToActiveStations();
			}
			else if (this.copyOption === 2) {
				this.copyToAllStations();
			}
		},

		copyToAllStations() {
			const newAllStationsList = this.allStationsList.map(station => {
				return {...station, ...this.formData};
			});

			this.$store.commit("setAssetManagementAllStationsList", newAllStationsList);
			this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
				message: "Station values copied to all stations."
			});
		},

		copyToActiveStations() {
			const newActiveStationList = this.activeStationsList.map(station => {
				return {...station, ...this.formData}
			});

			this.$store.commit("setAssetManagementActiveStationsList", newActiveStationList);
			this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
				message: "Station values copied to active stations."
			});
		}
	},

	watch: {
		selectedStation() {
			this.initStationData();
		}
	},

	mounted() {
		emitter.on("saveAsset", this.updateAsset);
		this.initStationData();
	},

	beforeUnmount() {
		emitter.off("saveAsset", this.updateAsset);
	}
}
</script>

<style scoped lang="scss">

</style>